import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { UilCheck, UilHome, UilCalendarAlt, UilPhone } from '@iconscout/react-unicons';
import { pricingService } from '../services/pricing/pricingService';

export default function CleaningForm() {
  const [step, setStep] = useState(0);
  const [formData, setFormData] = useState({ sqft: '', bedrooms: '', bathrooms: '', frequency: '' });
  const [firstTime, setFirstTime] = useState(null);
  const [errors, setErrors] = useState({});
  const [price, setPrice] = useState(null);
  const [loadingPrice, setLoadingPrice] = useState(false);

  const validateStep1 = () => {
    const errs = {};
    ['sqft', 'bedrooms', 'bathrooms', 'frequency'].forEach((f) => {
      if (!formData[f]) errs[f] = 'Required';
    });
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleNext = async () => {
    if (step === 0) {
      if (firstTime === 'yes') { window.location.href = 'tel:4258296092'; return; }
      setStep(1);
    } else if (step === 1) {
      if (validateStep1()) {
        setLoadingPrice(true);
        const result = await pricingService.calculatePrice(formData);
        if (result.success) { setPrice(result.data.price); setStep(2); }
        else alert(result.error || 'Error fetching price. Please try again.');
        setLoadingPrice(false);
      }
    }
  };

  const steps = ['Type', 'Details', 'Quote'];

  const inputCls = (field) =>
    `w-full px-4 py-3 border rounded-xl text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent ${
      errors[field] ? 'border-red-400 bg-red-50' : 'border-slate-200 bg-white hover:border-slate-300'
    }`;

  return (
    <div className="w-full max-w-lg mx-auto">
      {/* Step indicator */}
      <div className="flex items-center mb-8">
        {steps.map((label, idx) => (
          <React.Fragment key={idx}>
            <div className="flex flex-col items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold transition-all ${
                idx < step
                  ? 'bg-brand-600 text-white'
                  : idx === step
                    ? 'bg-brand-600 text-white ring-4 ring-brand-100'
                    : 'bg-slate-100 text-slate-400'
              }`}>
                {idx < step ? <UilCheck size={14} /> : idx + 1}
              </div>
              <span className={`text-xs mt-1.5 font-medium ${idx <= step ? 'text-brand-700' : 'text-slate-400'}`}>{label}</span>
            </div>
            {idx < steps.length - 1 && (
              <div className={`flex-1 h-0.5 mx-3 mb-4 transition-all ${idx < step ? 'bg-brand-500' : 'bg-slate-100'}`} />
            )}
          </React.Fragment>
        ))}
      </div>

      <AnimatePresence mode="wait">
        {step === 0 && (
          <motion.div key="s0" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.2 }}>
            <h3 className="text-lg font-bold text-slate-900 mb-1">Is this your first cleaning?</h3>
            <p className="text-sm text-slate-500 mb-6">First-time deep cleans are hourly. Regular cleanings get a flat rate.</p>
            <div className="grid grid-cols-2 gap-3 mb-4">
              <button
                onClick={() => setFirstTime('yes')}
                className={`p-5 border-2 rounded-2xl text-center transition-all ${
                  firstTime === 'yes' ? 'border-brand-500 bg-brand-50' : 'border-slate-200 hover:border-brand-300'
                }`}
              >
                <UilHome size={24} className="text-brand-600 mx-auto mb-2" />
                <p className="text-sm font-semibold text-slate-900">Yes, First Time</p>
                <p className="text-xs text-slate-500 mt-1">Hourly deep clean</p>
              </button>
              <button
                onClick={() => { setFirstTime('no'); handleNext(); }}
                className={`p-5 border-2 rounded-2xl text-center transition-all ${
                  firstTime === 'no' ? 'border-brand-500 bg-brand-50' : 'border-slate-200 hover:border-brand-300'
                }`}
              >
                <UilCalendarAlt size={24} className="text-brand-600 mx-auto mb-2" />
                <p className="text-sm font-semibold text-slate-900">Regular Cleaning</p>
                <p className="text-xs text-slate-500 mt-1">Instant flat-rate quote</p>
              </button>
            </div>
            {firstTime === 'yes' && (
              <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} className="bg-brand-50 border border-brand-200 rounded-xl p-4">
                <p className="text-sm text-brand-800 mb-3">For first-time deep cleans we'll discuss your needs over the phone.</p>
                <a href="tel:4258296092"
                  className="inline-flex items-center gap-2 bg-brand-600 hover:bg-brand-700 text-white text-sm font-semibold px-5 py-2.5 rounded-xl transition-colors">
                  <UilPhone size={16} /> Call (425) 829-6092
                </a>
              </motion.div>
            )}
          </motion.div>
        )}

        {step === 1 && (
          <motion.div key="s1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.2 }}>
            <h3 className="text-lg font-bold text-slate-900 mb-1">Tell us about your home</h3>
            <p className="text-sm text-slate-500 mb-6">We'll calculate an instant quote based on your home's details.</p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Square Footage</label>
                <input type="number" placeholder="e.g. 1500"
                  value={formData.sqft} onChange={(e) => setFormData({ ...formData, sqft: e.target.value })}
                  className={inputCls('sqft')} />
                {errors.sqft && <p className="text-xs text-red-500 mt-1">{errors.sqft}</p>}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Bedrooms</label>
                  <input type="number" placeholder="3"
                    value={formData.bedrooms} onChange={(e) => setFormData({ ...formData, bedrooms: e.target.value })}
                    className={inputCls('bedrooms')} />
                  {errors.bedrooms && <p className="text-xs text-red-500 mt-1">{errors.bedrooms}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Bathrooms</label>
                  <input type="number" placeholder="2"
                    value={formData.bathrooms} onChange={(e) => setFormData({ ...formData, bathrooms: e.target.value })}
                    className={inputCls('bathrooms')} />
                  {errors.bathrooms && <p className="text-xs text-red-500 mt-1">{errors.bathrooms}</p>}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Cleaning Frequency</label>
                <select value={formData.frequency} onChange={(e) => setFormData({ ...formData, frequency: e.target.value })}
                  className={inputCls('frequency')}>
                  <option value="">Select frequency…</option>
                  <option value="weekly">Weekly</option>
                  <option value="biweekly">Bi-weekly</option>
                  <option value="monthly">Monthly</option>
                </select>
                {errors.frequency && <p className="text-xs text-red-500 mt-1">{errors.frequency}</p>}
              </div>
            </div>
            <div className="flex justify-between items-center mt-6">
              <button onClick={() => setStep(0)} className="text-sm text-slate-500 hover:text-slate-700 font-medium transition-colors">← Back</button>
              <button onClick={handleNext} disabled={loadingPrice}
                className="bg-brand-600 hover:bg-brand-700 text-white text-sm font-semibold px-6 py-2.5 rounded-xl transition-colors disabled:opacity-50">
                {loadingPrice ? 'Calculating…' : 'Calculate Quote →'}
              </button>
            </div>
          </motion.div>
        )}

        {step === 2 && (
          <motion.div key="s2" initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.25 }}>
            <div className="text-center mb-6">
              <div className="w-14 h-14 bg-brand-50 rounded-full flex items-center justify-center mx-auto mb-3">
                <UilCheck size={28} className="text-brand-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-900">Your Estimate</h3>
              <p className="text-sm text-slate-500 mt-1">Based on your home details</p>
            </div>

            <div className="bg-slate-50 rounded-2xl p-5 mb-4 grid grid-cols-2 gap-3 text-sm">
              {[['Square Feet', `${formData.sqft} sqft`], ['Bedrooms', formData.bedrooms], ['Bathrooms', formData.bathrooms], ['Frequency', formData.frequency]].map(([k, v]) => (
                <div key={k}>
                  <p className="text-slate-500 text-xs">{k}</p>
                  <p className="font-semibold text-slate-900 capitalize">{v}</p>
                </div>
              ))}
            </div>

            <div className="bg-brand-600 rounded-2xl p-6 text-center text-white mb-4">
              <p className="text-brand-100 text-xs uppercase tracking-wider mb-2">Estimated Price</p>
              <p className="text-5xl font-bold tracking-tight">${price?.toFixed(2)}</p>
              <p className="text-brand-100 text-sm mt-1">per cleaning session</p>
            </div>

            <a href="tel:4258296092"
              className="flex items-center justify-center gap-2 w-full bg-brand-600 hover:bg-brand-700 text-white font-semibold py-3.5 rounded-xl transition-colors mb-3">
              <UilPhone size={18} /> Call to Book
            </a>
            <button onClick={() => { setStep(0); setFormData({ sqft: '', bedrooms: '', bathrooms: '', frequency: '' }); setFirstTime(null); setPrice(null); }}
              className="w-full py-2 text-sm text-slate-500 hover:text-slate-700 font-medium transition-colors">
              Start over
            </button>
            <p className="text-xs text-slate-400 text-center mt-3">Final pricing may vary based on home condition.</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
